import type { GroupMatch, GroupPreview, RuleGroup } from "../../shared/api.js";
import type { StackType } from "../../shared/stack.js";
import type { AppContext } from "../Page.js";
import { clear, debounce, h } from "../dom.js";
import { full, stackName } from "../format.js";
import { Dialog } from "./Dialog.js";
import { stackIcon } from "./StackIcon.js";

/** GregTech circuit ore names, one per tier (the same list as CIRCUIT_TIERS in CraftableSearch). */
const CIRCUIT_ORES =
  "circuitPrimitive, circuitBasic, circuitGood, circuitAdvanced, circuitData, circuitElite, circuitMaster, circuitUltimate, " +
  "circuitSuperconductor, circuitInfinite, circuitBio, circuitOptical, circuitExotic, circuitCosmic, circuitTranscendent";

const PRESETS: Array<{ label: string; name: string; match: GroupMatch }> = [
  { label: "All essentia", name: "Essentia", match: { type: "essentia" } },
  { label: "All GregTech circuits", name: "Circuits", match: { type: "item", ore: CIRCUIT_ORES } },
  { label: "All fluids", name: "Fluids", match: { type: "fluid" } },
];

const STATE_LABEL = {
  member: "",
  own: "has its own rule",
  other: "in group",
  excluded: "skipped",
};

/** "Essentia · ore circuit* · “hv”", for the Groups table. */
export function describeMatch(match: GroupMatch): string {
  const parts: string[] = [];
  if (match.type) parts.push({ item: "Items", fluid: "Fluids", essentia: "Essentia" }[match.type]);
  if (match.ore) parts.push(match.ore === CIRCUIT_ORES ? "GregTech circuits" : `ore ${match.ore}`);
  if (match.text) parts.push(`“${match.text}”`);
  return parts.join(" · ");
}

/** Create or edit a rule group in a dialog, with a live preview of what it covers. Resolves true when saved. */
export function editGroup(ctx: AppContext, existing: RuleGroup | null): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = new Dialog(existing ? "Edit group" : "New group", true);
    const exclude = new Set(existing?.exclude ?? []);
    const num = (value: number, min = 0) => h("input", { type: "number", min: String(min), value: String(value), required: true });
    const name = h("input", { value: existing?.name ?? "", required: true, placeholder: "Essentia" });
    const type = h(
      "select",
      null,
      h("option", { value: "" }, "Any type"),
      ...(["item", "fluid", "essentia"] as StackType[]).map((t) => h("option", { value: t }, t)),
    );
    type.value = existing?.match.type ?? "";
    const ore = h("input", { value: existing?.match.ore ?? "", placeholder: "circuit*, gemDiamond" });
    const text = h("input", { value: existing?.match.text ?? "", placeholder: "Words in the name, e.g. hv circuit" });
    const min = num(existing?.min ?? 1000);
    const target = num(existing?.target ?? 1000);
    const batch = num(existing?.batch ?? 256, 1);
    const priority = h("input", { type: "number", value: String(existing?.priority ?? 0) });
    const enabled = h("input", { type: "checkbox", checked: existing?.enabled ?? true });
    const critical = h("input", { type: "checkbox", checked: existing?.critical ?? false });
    const cpuNames = new Set([...ctx.live.cpus.map((c) => c.name), ...(existing?.cpus ?? [])]);
    const cpuBoxes = [...cpuNames]
      .filter((n) => !n.startsWith("#"))
      .map((cpu) => ({ name: cpu, box: h("input", { type: "checkbox", checked: existing?.cpus.includes(cpu) ?? false }) }));
    const previewInfo = h("p", { class: "muted small" });
    const previewList = h("ul", { class: "group-preview" });
    const error = h("p", { class: "error" });
    let saved = false;
    dialog.el.addEventListener("close", () => resolve(saved));

    const match = (): GroupMatch => ({
      ...(type.value ? { type: type.value as StackType } : {}),
      ...(ore.value.trim() ? { ore: ore.value.trim() } : {}),
      ...(text.value.trim() ? { text: text.value.trim() } : {}),
    });

    const renderPreview = (preview: GroupPreview) => {
      clear(previewList);
      if (!preview.craftables) {
        previewInfo.replaceChildren(
          "The craftables list isn't loaded yet, so there is nothing to preview. ",
          h("button", { type: "button", class: "link", onclick: () => void loadCraftables() }, "Load it from the game"),
          " (saving the group loads it too).",
        );
        return;
      }
      previewInfo.textContent =
        `${full(preview.members)} of ${full(preview.matches)} matching craftables get this group's rule.` +
        (preview.items.length < preview.matches ? ` Showing the first ${preview.items.length}.` : "") +
        " Untick one to skip it.";
      for (const item of preview.items) {
        const box = h("input", {
          type: "checkbox",
          checked: item.state === "member",
          disabled: item.state === "own" || item.state === "other",
          onchange: () => {
            if (box.checked) exclude.delete(item.key);
            else exclude.add(item.key);
            refresh();
          },
        });
        const note = item.state === "other" ? `${STATE_LABEL.other} ${item.group}` : STATE_LABEL[item.state];
        previewList.appendChild(
          h(
            "li",
            { class: item.state === "member" ? "" : "off" },
            h("label", { class: "check" }, box, stackIcon(item.stack), stackName(item.stack)),
            h("span", { class: "muted small" }, [item.hint, note].filter(Boolean).join(" · ")),
          ),
        );
      }
    };

    const refresh = debounce(async () => {
      const m = match();
      if (!m.type && !m.ore && !m.text) {
        clear(previewList);
        previewInfo.textContent = "Pick a type, ore names or words to see what the group covers.";
        return;
      }
      try {
        renderPreview(await ctx.api.previewGroup(m, [...exclude], existing?.id));
      } catch (err) {
        previewInfo.textContent = (err as Error).message;
      }
    }, 250);

    const loadCraftables = async () => {
      previewInfo.textContent = "Loading craftables from the game…";
      try {
        await ctx.api.refreshCraftables();
      } catch (err) {
        ctx.toast((err as Error).message, "error");
      }
      refresh();
    };

    for (const input of [type, ore, text]) input.addEventListener("input", refresh);

    const presets = existing
      ? null
      : h(
          "div",
          { class: "row wrap" },
          ...PRESETS.map((p) =>
            h(
              "button",
              {
                type: "button",
                class: "small",
                onclick: () => {
                  if (!name.value) name.value = p.name;
                  type.value = p.match.type ?? "";
                  ore.value = p.match.ore ?? "";
                  text.value = p.match.text ?? "";
                  refresh();
                },
              },
              p.label,
            ),
          ),
        );

    const form = h(
      "form",
      {
        class: "rule-form",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          error.textContent = "";
          const body = {
            name: name.value,
            match: match(),
            min: Number(min.value),
            target: Number(target.value),
            batch: Number(batch.value),
            priority: Number(priority.value),
            enabled: enabled.checked,
            critical: critical.checked,
            cpus: cpuBoxes.filter((c) => c.box.checked).map((c) => c.name),
            exclude: [...exclude],
          };
          try {
            if (existing) await ctx.api.updateGroup(existing.id, body);
            else await ctx.api.createGroup(body);
            saved = true;
            ctx.toast(existing ? "Group saved" : "Group added");
            dialog.close();
          } catch (err) {
            error.textContent = (err as Error).message;
          }
        },
      },
      h("label", null, "Name", name),
      h("h3", null, "Which craftables"),
      presets,
      h("div", { class: "row wrap" }, h("label", null, "Type", type), h("label", { class: "grow" }, "Ore dictionary (comma separated, * matches anything)", ore)),
      h("div", { class: "row" }, h("label", { class: "grow" }, "Name contains", text)),
      previewInfo,
      previewList,
      h("h3", null, "Amounts for each of them"),
      h(
        "div",
        { class: "row" },
        h("label", null, "Craft when below (min)", min),
        h("label", null, "Craft up to (target)", target),
        h("label", null, "Most per request (batch)", batch),
        h("label", null, "Priority", priority),
      ),
      h(
        "p",
        { class: "muted small" },
        "A stack with a rule of its own keeps it. Editing one of the group's rules on the Stock page makes it a rule of its own; deleting one makes the group skip it.",
      ),
      h("div", { class: "row" }, h("label", { class: "check" }, enabled, "Enabled"), h("label", { class: "check" }, critical, "Critical (stocked by the computer when the app is offline)")),
      cpuBoxes.length
        ? h("fieldset", null, h("legend", null, "Only use these CPUs (none checked = any)"), h("div", { class: "row wrap" }, ...cpuBoxes.map((c) => h("label", { class: "check" }, c.box, c.name))))
        : null,
      error,
    );
    dialog.body.appendChild(form);
    dialog.footer.append(
      h("button", { type: "button", onclick: () => dialog.close() }, "Cancel"),
      h("button", { type: "button", class: "primary", onclick: () => form.requestSubmit() }, existing ? "Save" : "Add group"),
    );
    dialog.open();
    refresh();
    name.focus();
  });
}
